"use client";
import './i18n/autoTranslate';

import React from 'react';
import { 
  Target, Zap, Cpu, Users, Award, Globe as Global, 
  FileText, Video, BarChart, Sparkles, 
  Shield, TrendingUp, MessageSquare, Bot,
  ArrowRight, Play, Star, CheckCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Globe from '../components/lightswind/globe';
import SEO from './SEO';

// Animation Components
const SectionWrapper = ({ children, className = "" }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      viewport={{ once: true }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const GlassCard = ({ children, className = "", hover = true }) => {
  return (
    <motion.div
      whileHover={hover ? { y: -8, transition: { duration: 0.3 } } : {}}
      className={`
        backdrop-blur-xl 
        bg-white/5 
        border border-white/10 
        rounded-3xl
        shadow-[0_0_40px_rgba(108,99,255,0.08)]
        ${hover ? 'hover:bg-white/8 hover:border-white/20 hover:shadow-[0_0_60px_rgba(108,99,255,0.15)]' : ''}
        transition-all duration-300
        ${className}
      `}
    >
      <img src={"/assets/images/Gemini_Generated_6_dlmovh.png"} className="absolute -inset-0 object-cover w-full -z-10 h-full rounded-3xl opacity-[0.09]" alt="" />
      
      {children}
    </motion.div>
  );
};

const StaggerContainer = ({ children, className = "" }) => {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={{
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: {
            staggerChildren: 0.1,
            delayChildren: 0.2
          }
        }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const StaggerItem = ({ children, index = 0, className = "" }) => {
  const direction = index % 2 === 0 ? -100 : 100;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: direction, y: 100 }}
      whileInView={{opacity: 1, x: 0, y: 0}}
      transition= {{
        duration: 2,
        ease: [0.22, 1, 0.36, 1]
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen bg-[#07070A] text-white overflow-hidden">
      <SEO
        title="About InterviewGenieAI — AI Interview Preparation Platform"
        description="InterviewGenieAI helps job seekers prepare for interviews with AI-powered practice, real-time feedback, resume insights, analytics, and confidence-building tools."
        canonical="https://interviewgenieai.com/about"
      />
      <div className='w-full  opacity-[0.2]  relative'>
        <Globe
          size='full'
          theta={0.2}
          dark={1}
          scale={1.1}
          diffuse={1.5}
          baseColor="#1a1a1a"
          markerColor="#6C63FF"
          glowColor="#444444"
          className="absolute"
        />
      </div>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#6C63FF]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#8B7CFF]/5 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-3 mb-8"
            >
              <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
              <span className="text-sm font-medium tracking-wider text-[#6C63FF] uppercase">
                Redefining Interview Preparation
              </span>
              <div className="w-8 h-px bg-gradient-to-r from-transparent via-[#6C63FF] to-transparent" />
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-8"
            >
              <span className="bg-gradient-to-r from-white via-white/90 to-white bg-clip-text text-transparent">
                About Interview
              </span>
              <br />
              <span className="bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] bg-clip-text text-transparent">
                GenieAI
              </span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-12"
            >
              We're building the future of interview preparation—where artificial intelligence meets human potential to create confident, skilled, and successful candidates.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/pricing")}
                style={{
                  boxShadow: 'inset 0 -4px 10px rgba(255, 255, 255, 0.3), 0 0 40px rgba(108, 99, 255, 0.3)'
                }}
                className="px-8 py-2 group rounded-xl bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] text-white font-medium transition-all duration-300 hover:shadow-[0_0_50px_rgba(108,99,255,0.4)] flex items-center justify-center gap-3 relative overflow-hidden"
              >
                <div 
                  className="absolute -bottom-1 left-[20%] w-1/2 right-0 h-4 rounded-full bg-white/70 blur-[10px] pointer-events-none"
                />
                <Zap className="group-hover:animate-pulse group-hover:translate-x-1 transition-transform" size={18} />
                <span className="relative z-10">Start Free Trial</span>
              </motion.button>

              <button 
                onClick={() => navigate("/features")}
                className="px-8 py-2 rounded-xl bg-white/5 border border-white/10 text-white font-semibold hover:bg-white/10 transition-all duration-300 flex items-center justify-center gap-3"
              >
                <Play className="w-5 h-5" />
                See Platform Tour
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionWrapper className="mb-16">
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-6">
                Our Mission
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
                To democratize interview excellence through AI-powered precision, making professional success accessible to every candidate, regardless of background or experience level.
              </p>
            </div>
          </SectionWrapper>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Target,
                title: "Realistic Preparation",
                description: "Simulating actual interview scenarios with industry-specific questions and environments"
              },
              {
                icon: TrendingUp,
                title: "Data-Driven Growth",
                description: "Using AI analytics to provide actionable insights and measurable improvement"
              },
              {
                icon: Shield,
                title: "Confidence Building",
                description: "Creating safe spaces for practice that build genuine confidence and reduce anxiety"
              },
              {
                icon: Global,
                title: "Global Accessibility",
                description: "Making professional interview preparation available to candidates worldwide"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="p-8 h-full">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-2xl border border-white/20 bg-gradient-to-br from-white/5 to-white/2 flex items-center justify-center mb-6">
                      <item.icon className="w-8 h-8 text-[#6C63FF]" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4">
                      {item.title}
                    </h3>
                    <p className="text-gray-400">
                      {item.description}
                    </p>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes Us Different Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent via-[#6C63FF]/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <SectionWrapper className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-6">
              What Makes InterviewGenieAI Different
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              We combine cutting-edge AI with deep understanding of interview dynamics to deliver truly transformative preparation experiences.
            </p>
          </SectionWrapper>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: FileText,
                title: "Resume-to-Interview Personalization",
                description: "Our AI analyzes your resume to create hyper-personalized interview scenarios that reflect your actual experience and skills."
              },
              {
                icon: Zap,
                title: "Real-Time AI Feedback",
                description: "Get instant analysis on structure, clarity, confidence, and filler words as you practice, with actionable improvement suggestions."
              },
              {
                icon: Video,
                title: "Multi-Modal Analysis",
                description: "Comprehensive evaluation across video, voice, and text interviews to develop complete communication mastery."
              },
              {
                icon: Target,
                title: "Role & Company Specificity",
                description: "Practice with questions and scenarios tailored to specific companies and roles for maximum relevance and preparation."
              },
              {
                icon: BarChart,
                title: "Deep Performance Analytics",
                description: "Track progress over time with detailed metrics and identify areas for improvement with data-driven insights."
              },
              {
                icon: Sparkles,
                title: "AI-Generated Improvement Plans",
                description: "Receive personalized roadmaps with daily exercises, practice recommendations, and skill development strategies."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="p-8 h-full">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl border border-white/20 bg-gradient-to-br from-white/5 to-white/2 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-6 h-6 text-[#6C63FF]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-gray-400">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionWrapper className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-6">
              Who We Serve
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              InterviewGenieAI is designed for every stage of the career journey, from first interviews to executive leadership transitions.
            </p>
          </SectionWrapper>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: "Students & Graduates",
                description: "Master entry-level interviews with resume optimization and foundational communication skill development.",
                audience: "University students, recent graduates, internship seekers"
              },
              {
                icon: Target,
                title: "Job Seekers & Career Switchers",
                description: "Navigate career transitions with confidence through role-specific preparation and industry insights.",
                audience: "Mid-career professionals, industry switchers, promotion candidates"
              },
              {
                icon: Award,
                title: "Executives & Leaders",
                description: "Prepare for leadership interviews with strategic communication analysis and executive presence development.",
                audience: "Senior managers, directors, C-suite candidates"
              },
              {
                icon: Global,
                title: "Global & Remote Candidates",
                description: "Excel in cross-border interviews with cultural awareness and remote communication optimization.",
                audience: "International job seekers, remote role candidates"
              },
              {
                icon: MessageSquare,
                title: "Mentors & Career Coaches",
                description: "Enhance your coaching with AI-powered insights, progress tracking, and professional report generation.",
                audience: "Career coaches, HR professionals, university counselors"
              },
              {
                icon: Bot,
                title: "Technical Professionals",
                description: "Ace technical interviews with code review simulations, system design practice, and technical communication analysis.",
                audience: "Software engineers, data scientists, technical leads"
              }
            ].map((audience, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="p-8 h-full">
                  <div className="flex flex-col h-full">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 rounded-xl border border-white/20 bg-gradient-to-br from-white/5 to-white/2 flex items-center justify-center">
                        <audience.icon className="w-6 h-6 text-[#6C63FF]" />
                      </div>
                      <h3 className="text-xl font-bold text-white">
                        {audience.title}
                      </h3>
                    </div>
                    <p className="text-gray-400 mb-6 flex-grow">
                      {audience.description}
                    </p>
                    <div className="pt-6 border-t border-white/10">
                      <p className="text-sm text-gray-500">
                        <span className="font-medium text-gray-400">Primary Audience:</span> {audience.audience}
                      </p>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Vision Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent via-[#8B7CFF]/5 to-transparent">
        <div className="max-w-7xl mx-auto">
          <SectionWrapper>
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-6">
                Our Vision
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-12">
                To become the global standard for interview readiness, transforming how candidates prepare and how companies identify talent.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-bold text-white mb-8">
                  Shaping the Future of Interview Preparation
                </h3>
                <div className="space-y-6">
                  {[
                    "Pioneering AI-driven interview evaluation that's both accurate and empathetic",
                    "Creating skill-based assessment systems that reduce bias and increase opportunity",
                    "Building comprehensive career development ecosystems that extend beyond interviews",
                    "Establishing global standards for interview preparation and performance evaluation",
                    "Developing adaptive learning systems that grow with candidates throughout their careers"
                  ].map((vision, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-start gap-4"
                    >
                      <div className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="w-4 h-4 text-[#6C63FF]" />
                      </div>
                      <p className="text-gray-300">
                        {vision}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              <div>
                <GlassCard className="p-8">
                  <div className="space-y-6">
                    <h4 className="text-2xl font-bold text-white">
                      By 2030, We Aim To:
                    </h4>
                    <div className="space-y-4">
                      {[
                        { value: "10M+", label: "Candidates Trained Worldwide" },
                        { value: "95%", label: "Interview Success Rate Improvement" },
                        { value: "50+", label: "Languages Supported" },
                        { value: "1000+", label: "Company-Specific Interview Modules" }
                      ].map((goal, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: index * 0.1 }}
                          viewport={{ once: true }}
                          className="flex items-center justify-between p-4 rounded-xl bg-white/5"
                        >
                          <span className="text-gray-300">{goal.label}</span>
                          <span className="text-2xl font-bold text-white">{goal.value}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </GlassCard>
              </div>
            </div>
          </SectionWrapper>
        </div>
      </section>

      {/* Trust & Professionalism Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionWrapper className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-6">
              Enterprise-Grade Trust & Professionalism
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Built with the security, reliability, and professionalism that enterprise users expect.
            </p>
          </SectionWrapper>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: "Data Privacy",
                description: "Enterprise-grade encryption and strict data protection protocols ensure your information remains secure and private."
              },
              {
                icon: FileText,
                title: "Professional Outputs",
                description: "Generate polished PDF reports, performance summaries, and improvement plans ready for review by mentors and hiring managers."
              },
              {
                icon: BarChart,
                title: "Actionable Analytics",
                description: "Data-driven insights presented in clear, professional formats that facilitate meaningful progress discussions."
              },
              {
                icon: Users,
                title: "Team & Enterprise Ready",
                description: "Scalable solutions for universities, corporations, and coaching businesses with custom branding and admin controls."
              }
            ].map((trust, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <GlassCard className="p-8 h-full">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-2xl border border-white/20 bg-gradient-to-br from-white/5 to-white/2 flex items-center justify-center mx-auto mb-6">
                      <trust.icon className="w-8 h-8 text-[#6C63FF]" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4">
                      {trust.title}
                    </h3>
                    <p className="text-gray-400">
                      {trust.description}
                    </p>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <SectionWrapper>
            <GlassCard className="p-12 text-center">
              <div className="relative">
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#6C63FF]/5 rounded-full blur-3xl" />
                <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-[#8B7CFF]/5 rounded-full blur-3xl" />
                
                <div className="relative z-10">
                  <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-white/90 bg-clip-text text-transparent mb-6">
                    Ready to Transform Your Interview Journey?
                  </h2>
                  <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
                    Join thousands of successful candidates who have mastered their interviews with AI-powered precision.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-6 justify-center">
                    <button 
                      onClick={() => navigate("/pricing")}
                      className="px-10 py-5 rounded-xl bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] text-white font-semibold text-lg hover:shadow-[0_0_60px_rgba(108,99,255,0.5)] transition-all duration-300 flex items-center justify-center gap-3"
                    >
                      <Zap className="w-5 h-5" />
                      Start Practicing Smarter
                      <ArrowRight className="w-5 h-5" />
                    </button>
                    
                    <button 
                      onClick={() => navigate("/contact")}
                      className="px-10 py-5 rounded-xl bg-white/5 border border-white/10 text-white font-semibold text-lg hover:bg-white/10 transition-all duration-300 flex items-center justify-center gap-3"
                    >
                      <MessageSquare className="w-5 h-5" />
                      Talk to Our Team
                    </button>
                  </div>
                  
                  <p className="text-sm text-gray-500 mt-8 font-medium tracking-wider">
                    No credit card required • 7-day free trial • Cancel anytime
                  </p>
                </div>
              </div>
            </GlassCard>
          </SectionWrapper>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-4 sm:px-6 lg:px-8 bg-black/30 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <StaggerContainer className="grid md:grid-cols-4 gap-8 mb-12">
            <StaggerItem index={0}>
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <img src={"/GenieAI_2.png"} className="text-white" />
                  </div>
                  <span className="font-bold text-white">InterviewGenieAI</span>
                </div>
                <p className="text-gray-400 text-sm">The most advanced AI interview preparation platform</p>
              </div>
            </StaggerItem>
            
            {[
              { title: "Product", items: ["Features", "How It Works", "Pricing", "Demo"] },
              { title: "Resources", items: ["Blog", "Tutorials", "Documentation", "Support"] },
              { title: "Company", items: ["About", "Careers", "Contact", "Legal"] }
            ].map((section, index) => (
              <StaggerItem key={index} index={index + 1}>
                <div>
                  <h4 className="font-bold mb-4 text-white">{section.title}</h4>
                  <ul className="space-y-2 text-sm">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex}>
                        <a href="#" className="text-gray-400 hover:text-[#6C63FF] transition-colors">
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center"
          >
            <p className="text-gray-400 text-sm">© 2025 InterviewGenieAI. All rights reserved.</p>
            <div className="flex items-center gap-6 mt-4 md:mt-0">
              {["Terms", "Privacy", "Cookies"].map((item, index) => (
                <a key={index} href="#" className="text-gray-400 hover:text-[#6C63FF] transition-colors text-sm">
                  {item}
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;
